import { useState, useRef, useEffect } from "react";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bot, User as UserIcon, Send, Loader2, Sparkles } from "lucide-react";

export default function DashboardAdvisor({ userId }) {
  const [sessionId] = useState(() => {
    const k = "nirmantra_dash_advisor_" + (userId || "u");
    let s = localStorage.getItem(k);
    if (!s) { s = "dash_" + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem(k, s); }
    return s;
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef();

  useEffect(() => { fetch(`${API}/public/chat/${sessionId}`).then((r) => r.json()).then((d) => Array.isArray(d) && setMessages(d)).catch(() => {}); }, [sessionId]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const resp = await fetch(`${API}/public/chat`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sessionId, message: q }) });
      const reader = resp.body.getReader(); const dec = new TextDecoder(); let buf = "";
      while (true) {
        const { done, value } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true }); const parts = buf.split("\n\n"); buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data:")) continue;
          const data = JSON.parse(part.slice(5).trim());
          if (data.delta) setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + data.delta }; return c; });
        }
      }
    } catch { /* noop */ } finally { setStreaming(false); }
  };

  return (
    <div id="advisor" className="card-premium rounded-2xl flex flex-col h-[460px] overflow-hidden" data-testid="dashboard-advisor">
      <div className="flex items-center gap-2.5 px-5 py-4 bg-[#0B1B2B] text-white">
        <div className="h-9 w-9 rounded-xl bg-[#0E9AC8] flex items-center justify-center"><Bot className="h-5 w-5" strokeWidth={1.75} /></div>
        <div><p className="font-display font-bold leading-tight">AI Construction Advisor</p><p className="text-[11px] text-[#4FD1DB] flex items-center gap-1"><Sparkles className="h-3 w-3" /> Powered by NCIS™</p></div>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4 bg-[#FBFAF9]">
        {messages.length === 0 && (
          <div className="text-center py-10">
            <p className="font-display font-bold text-slate-900">Ask me anything about your build</p>
            <p className="text-sm text-slate-500 mt-1">Create a project to unlock document analysis & a health report — or just start chatting.</p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} data-testid={`advisor-msg-${m.role}`} className={`flex gap-2.5 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
            <div className={`h-8 w-8 shrink-0 rounded-lg flex items-center justify-center ${m.role === "user" ? "bg-slate-900" : "bg-[#0E9AC8]"}`}>
              {m.role === "user" ? <UserIcon className="h-4 w-4 text-white" strokeWidth={1.75} /> : <Bot className="h-4 w-4 text-white" strokeWidth={1.75} />}
            </div>
            <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap font-sans ${m.role === "user" ? "bg-slate-900 text-white rounded-tr-sm" : "bg-white border border-slate-100 text-slate-700 rounded-tl-sm"}`}>
              {m.content || <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
            </div>
          </div>
        ))}
      </div>
      <div className="border-t border-slate-200 p-3 flex gap-2 bg-white">
        <input data-testid="advisor-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Ask the AI advisor…" className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-[#0E9AC8] focus:ring-2 focus:ring-[#0E9AC8]/15" />
        <Button data-testid="advisor-send" onClick={() => send()} disabled={streaming} className="bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl h-11 w-11 p-0 shrink-0">
          {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" strokeWidth={1.75} />}
        </Button>
      </div>
    </div>
  );
}
